import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import apiClient from '../services/apiClient'

// ─── Types ────────────────────────────────────────────────────────────────────
interface WorkshopSummary {
  id: number
  slug: string
  title: string
  description: string | null
  thumbnail_url: string | null
  held_at: string
  duration_min: number | null
  youtube_url: string | null
  status: 'upcoming' | 'completed'
  material_count: number
  whiteboard_count: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr: string) {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('vi-VN', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function YouTubeIcon() {
  return (
    <svg
      className="w-5 h-5 fill-current"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────
function WorkshopCard({ w }: { w: WorkshopSummary }) {
  return (
    <article className="group relative flex flex-col rounded-2xl bg-m2-surface border border-m2-outline-variant shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      {/* Thumbnail */}
      <div className="relative h-44 overflow-hidden bg-gradient-to-br from-m2-primary-container/30 to-m2-surface-high flex items-center justify-center">
        {w.thumbnail_url ? (
          <img
            src={w.thumbnail_url}
            alt={w.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 opacity-40">
            <span className="text-5xl">🎓</span>
          </div>
        )}
        {/* Badge */}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-m2-surface/90 backdrop-blur-sm border border-m2-outline-variant shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-m2-secondary" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-m2-on-surface-variant">
            Đã diễn ra
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        {/* Date */}
        <p className="text-xs text-m2-on-surface-variant mb-2 flex items-center gap-1.5">
          <span>📅</span>
          {formatDate(w.held_at)}
          {w.duration_min && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-m2-surface-low text-m2-on-surface-variant border border-m2-outline-variant text-[10px]">
              {w.duration_min} phút
            </span>
          )}
        </p>

        <h2 className="font-bold text-base text-m2-primary mb-2 leading-snug line-clamp-2">
          {w.title}
        </h2>

        {w.description && (
          <p className="text-xs text-m2-on-surface-variant leading-relaxed line-clamp-2 mb-3">
            {w.description}
          </p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-[11px] text-m2-on-surface-variant mt-auto mb-4">
          {w.material_count > 0 && (
            <span className="flex items-center gap-1">
              📄 {w.material_count} tài liệu
            </span>
          )}
          {w.whiteboard_count > 0 && (
            <span className="flex items-center gap-1">
              🖊️ {w.whiteboard_count} bảng vẽ
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-m2-outline-variant">
          {w.youtube_url && (
            <a
              href={w.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border-2 border-red-500 text-red-600 hover:bg-red-50 transition-colors"
            >
              <YouTubeIcon />
              Xem video
            </a>
          )}
          <Link
            to={`/workshops/${w.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-m2-primary text-m2-on-primary hover:opacity-90 transition-opacity ml-auto"
          >
            Chi tiết →
          </Link>
        </div>
      </div>
    </article>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const WorkshopsListPage = () => {
  const [workshops, setWorkshops] = useState<WorkshopSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get<{ success: boolean; data: WorkshopSummary[] }>(
          '/public/workshops-list'
        )
        if (res.data?.success) setWorkshops(res.data.data)
        else setError('Không tải được danh sách workshop.')
      } catch {
        setError('Không tải được danh sách workshop.')
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [])

  return (
    <div className="min-h-screen bg-m2-background" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-m2-outline-variant bg-gradient-to-b from-m2-surface-low via-m2-surface to-m2-background">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgb(var(--m2-primary)/0.10) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 bg-m2-surface border border-m2-primary-container shadow-sm">
            <span className="w-2 h-2 rounded-full bg-m2-secondary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-m2-on-surface-variant">
              Workshop Archive
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-m2-primary">
            Thư viện Workshop
          </h1>
          <p className="text-base md:text-lg text-m2-on-surface-variant max-w-2xl mx-auto leading-relaxed">
            Xem lại toàn bộ các buổi workshop đã diễn ra — video, tài liệu và bảng minh họa đều có
            sẵn để bạn học lại bất cứ lúc nào.
          </p>
        </div>
      </section>

      {/* ── List ── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl bg-m2-surface border border-m2-outline-variant h-64 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20">
            <p className="text-m2-on-surface-variant">{error}</p>
          </div>
        )}

        {!loading && !error && workshops.length === 0 && (
          <div className="text-center py-20">
            <p className="text-4xl mb-4">🎓</p>
            <p className="text-m2-on-surface-variant text-lg font-medium">
              Chưa có workshop nào được công bố.
            </p>
            <p className="text-sm text-m2-on-surface-variant mt-2">
              Hãy quay lại sau để xem các buổi workshop sắp tới!
            </p>
          </div>
        )}

        {!loading && !error && workshops.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map((w) => (
              <WorkshopCard key={w.id} w={w} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default WorkshopsListPage
